import { useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import Popover from '@rsos/components/capstone/base/Popover';
import type { KeywordSet } from '@rsos/reduxDataStore';
import type { AppState } from '../../../../../../types/AppState';
import CopyKeywords from '../Keywords/CopyKeywords';
import PoweredByHarmony from '../PoweredByHarmony';
import {
  TranscriptionStateKeywordSetMatch,
  setSelectedKeywordMatch,
  useSelectedKeywordMatch,
  useAllKeywordMatches,
} from '../transcriptionSlice';
import {
  Keyword,
  KeywordPopoverText,
  KeywordsBox,
  KeywordsContainer,
  KeywordsContent,
  KeywordsHeader,
  KeywordText,
  PopoverBox,
  PoweredByHarmonyWrapper,
  CopyKeywordsWrapper,
} from './KeywordMatches.styles';
import { trackKeywordHovered, trackKeywordClicked } from './KeywordTracking';

export const KeywordMatches = () => {
  const dispatch = useDispatch();
  const matches = useAllKeywordMatches();
  const selectedKeywordMatch = useSelectedKeywordMatch();
  const selectedCallerID = useSelector(
    (state: AppState) => state.emergencyCalls.selectedCallerID,
  ) as string;
  const NOTHING_HOVERED = '';
  const [popoverHoverSetName, setPopoverHoverSetName] =
    useState(NOTHING_HOVERED);

  if (!matches || matches.length <= 0) {
    return <></>;
  }

  const handleHover = (match: TranscriptionStateKeywordSetMatch | null) => {
    if (!match) {
      setPopoverHoverSetName(NOTHING_HOVERED);
      return;
    }
    trackKeywordHovered(
      match.keyword_set as KeywordSet,
      match.matches.map(m => m.keyword),
    );
    setPopoverHoverSetName(match.keyword_set.name);
  };

  const isPopoverVisibleFor = (setName: string) =>
    popoverHoverSetName === setName;

  const handleKeywordClick = (
    matchedSet: TranscriptionStateKeywordSetMatch,
  ) => {
    if (
      selectedKeywordMatch &&
      selectedKeywordMatch.keywordSet.keyword_set.id ===
        matchedSet.keyword_set.id
    ) {
      dispatch(
        setSelectedKeywordMatch({
          callerId: selectedCallerID,
          match: null,
        }),
      );
    } else {
      trackKeywordClicked(matchedSet.keyword_set as KeywordSet);
      dispatch(
        setSelectedKeywordMatch({
          callerId: selectedCallerID,
          match: { keywordSetID: matchedSet.keyword_set.id, matchIndex: 0 },
        }),
      );
    }
  };

  // Extract all unique keywords from matches for the copy button
  const allKeywords = matches
    .flatMap(matchedSet => matchedSet.matches.map(match => match.keyword))
    .filter((keyword, index, self) => self.indexOf(keyword) === index);

  return (
    <KeywordsContainer data-name="keyword-matches-container">
      <KeywordsBox data-name="keyword-maches-card">
        <KeywordsHeader data-name="keywords-header">
          <PoweredByHarmonyWrapper>
            <PoweredByHarmony title="Keywords" />
          </PoweredByHarmonyWrapper>
          <CopyKeywordsWrapper>
            <CopyKeywords keywords={allKeywords} />
          </CopyKeywordsWrapper>
        </KeywordsHeader>
        <KeywordsContent data-name="keywords-content">
          {matches.map(matchedSet => {
            const isSelected =
              selectedKeywordMatch &&
              selectedKeywordMatch.keywordSet.keyword_set.id ===
                matchedSet.keyword_set.id;
            return (
              <Keyword
                key={matchedSet.keyword_set.id}
                onMouseEnter={() => handleHover(matchedSet)}
                onMouseLeave={() => handleHover(null)}
                onClick={() => handleKeywordClick(matchedSet)}
                isSelected={isSelected}
              >
                <Popover
                  cssDisplayProperty="block"
                  data-name={`keyword-match-popover-${matchedSet.keyword_set.name}`}
                  popoverID={`keyword-match-popover-${matchedSet.keyword_set.name}`}
                  isOpen={isPopoverVisibleFor(matchedSet.keyword_set.name)}
                  trigger={
                    <KeywordText
                      data-name={`keyword-matched-set-${matchedSet.keyword_set.name}`}
                    >
                      {matchedSet.keyword_set.name}
                    </KeywordText>
                  }
                  useCenterXOffset={true}
                  useCenterYOffset={false}
                  useCustomOffset={true}
                  yOffset={-50}
                >
                  <PopoverBox>
                    {matchedSet.matches
                      .map(match => match.keyword)
                      .filter((keyword, index, self) => {
                        // Filter out duplicates
                        return self.indexOf(keyword) === index;
                      })
                      .map((keyword, index) => (
                        <KeywordPopoverText
                          key={index}
                          data-name={`matched-keyword-${index}`}
                        >
                          {keyword}
                        </KeywordPopoverText>
                      ))}
                  </PopoverBox>
                </Popover>
              </Keyword>
            );
          })}
        </KeywordsContent>
      </KeywordsBox>
    </KeywordsContainer>
  );
};
